import { AppButton, AppModal, AppSpin } from '@/components/atoms';
import { CollapsibleCard } from '@/components/molecules';
import { SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useBillPatientListPdf } from '@/hooks/main/useBillPatientListPdf';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  useGaijiListPdf,
  useKakutei,
  useResetRezeptPrint,
  useRezeptList,
  useSearchRezeptMassagers,
  useSetAddTreatmentReport,
} from '@/hooks/main/useRezeptInit';
import { useRezeptIssue } from '@/hooks/main/useRezeptIssue';
import { useStartVisitCheckMutation } from '@/hooks/main/useVisitCheckMutation';
import useAppModal from '@/hooks/useAppModal';
import useAppToast from '@/hooks/useAppToast';
import {
  readVisitSummaryError,
  useVisitSummaryAvailability,
  useVisitSummaryPdf,
} from '@/hooks/main/useVisitSummary';
import type { KakuteiConfirmData } from '@/services/main/rezept/rezeptListService';
import type { MassagerOption, RezeptListItem, SearchRadio } from '@/types/rezept.types';
import type { VisitCheckFilters } from '@/types/visitCheck.types';
import { normalizeYearmonth } from '@/utils/dateConverter';
import { IconUtils } from '@/utils';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { hashKey, useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import EditRezeptStatusModal from './components/EditRezeptStatusModal';
import LongTermPatientsModal from './components/LongTermPatientsModal';
import OutcomeEditModal from './components/OutcomeEditModal';
import PrintAdjustModal from './components/PrintAdjustModal';
import RezeptListActionBar from './components/RezeptListActionBar';
import RezeptListTable from './components/RezeptListTable';
import RezeptSearchFilters from './components/RezeptSearchFilters';
import { INITIAL_SEARCH, SEARCH_RADIO_CLEAR_MAP } from './constants';
import { useRezeptInitData } from './hooks/useRezeptInitData';
import type { SearchState } from './types';
import { getDuplicateTreatmentReportFeeIds } from './utils';

const DUPLICATE_TREATMENT_REPORT_FEE_MESSAGE =
  'この受診者は同じ施術月のレセプト全てに施術報告書交付料が算定されています。いずれか1件のレセプトで算定して下さい。';

const RezeptListPage: React.FC = () => {
  const toast = useAppToast();
  const appModal = useAppModal();
  const queryClient = useQueryClient();
  const { tenant, clinicNumber } = useClinicContext();
  const { serviceType } = useTheme();
  const isR2 = serviceType === SERVICE_TYPE_R2;
  const { issueRezepts, isProcessing: isIssuing } = useRezeptIssue();
  const showInvalidYearmonthAlert = useCallback(() => {
    appModal.error({ title: '【提出年月】を正しく入力してください', okText: 'OK' });
  }, [appModal]);

  const [search, setSearch] = useState<SearchState>(INITIAL_SEARCH);
  const [appliedParams, setAppliedParams] = useState<Record<string, unknown> | null>(null);

  // 令和8年法改正 No 7 — 訪問施術総括表.
  //
  // Two separate conditions, easy to conflate:
  //   the button is SHOWN only when the month billed 訪問施術料4/5 somewhere — a practice
  //     with nothing to file gets no button at all, not a disabled one
  //     (客先確定 2026-08-17, feedback No.72);
  //   PRINTING is refused by the server until every rezept of the submission month is
  //     確定, and it returns the message to show.
  //
  // The months come from appliedParams, not from the live form: the button has to
  // describe the list that is on screen. Reading the form made it flip the moment the
  // 提出年月 picker changed, so the button and the rows below it disagreed until 検索 was
  // pressed. Patient filters are deliberately left out — the report always covers the
  // whole month (不具合No.71).
  const visitSummaryParams = useMemo(() => {
    const applied = (appliedParams ?? {}) as {
      search_yearmonth?: unknown;
      search_visitmonth?: unknown;
    };

    return {
      search_yearmonth: applied.search_yearmonth ? String(applied.search_yearmonth) : undefined,
      search_visitmonth: applied.search_visitmonth ? String(applied.search_visitmonth) : undefined,
    };
  }, [appliedParams]);
  const { data: hasVisitSummary } = useVisitSummaryAvailability(
    visitSummaryParams,
    appliedParams !== null
  );
  const visitSummaryPdf = useVisitSummaryPdf();
  const handleVisitSummary = useCallback(() => {
    visitSummaryPdf.mutate(visitSummaryParams, {
      onError: async err => {
        toast.error(await readVisitSummaryError(err, '訪問施術総括表の印刷に失敗しました。'));
      },
    });
  }, [visitSummaryPdf, visitSummaryParams, toast]);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [tenkiRezeptId, setTenkiRezeptId] = useState<number | null>(null);
  const [printAdjustOpen, setPrintAdjustOpen] = useState(false);
  const [longTermOpen, setLongTermOpen] = useState(false);
  const [kakuteiConfirmData, setKakuteiConfirmData] = useState<KakuteiConfirmData | null>(null);
  const [kakuteiCompleteOpen, setKakuteiCompleteOpen] = useState(false);
  // Edit record's status modal — opened by double-click row.
  const [editStatusRezept, setEditStatusRezept] = useState<RezeptListItem | null>(null);

  const { param, isLoading } = useRezeptInitData({ setSearch });
  const {
    data: listData,
    isLoading: isListLoading,
    refetch: refetchList,
  } = useRezeptList(appliedParams);
  const { searchMassagersMutation } = useSearchRezeptMassagers();
  const { saveAddTreatmentReport } = useSetAddTreatmentReport();
  const { resetPrint, isResetting } = useResetRezeptPrint();
  const { kakuteiInit, kakuteiExecute, isIniting, isExecuting } = useKakutei();
  const [massagerOptions, setMassagerOptions] = useState<MassagerOption[]>([]);
  const rows = listData?.rezepts ?? [];
  const duplicateTreatmentReportFeeIds = useMemo(
    () => getDuplicateTreatmentReportFeeIds(rows),
    [rows]
  );

  useEffect(() => {
    if (!search.search_yearmonth) {
      setMassagerOptions([]);
      return;
    }
    searchMassagersMutation(search.search_yearmonth).then(list => setMassagerOptions(list));
  }, [search.search_yearmonth, searchMassagersMutation]);

  // BD §3 Rezept list: show data matching the default filter on screen entry — no need
  // for the user to click 検索. Auto-apply once init is done (search_yearmonth is set).
  useEffect(() => {
    if (search.search_yearmonth && appliedParams === null) {
      setAppliedParams(buildSearchPayload());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search.search_yearmonth]);

  const handleFieldChange = useCallback(
    <K extends keyof SearchState>(key: K, value: SearchState[K]) => {
      setSearch(prev => ({ ...prev, [key]: value }));
    },
    []
  );

  const handleSearchRadioChange = useCallback(
    (next: SearchRadio) => {
      setSearch(prev => {
        const cleared = SEARCH_RADIO_CLEAR_MAP[next];
        const nextState: SearchState = { ...prev, search_radio: next };
        cleared.forEach(field => {
          if (field === 'search_radio' || field === 'sort_radio') return;
          if (field === 'insurer_name') nextState.insurer_name = '';
          else if (field === 'serial_number') nextState.serial_number = '';
          else if (field === 'insurer_type_id') nextState.insurer_type_id = '';
          else if (field === 'ini') nextState.ini = '';
          else if (field === 'patient_name') nextState.patient_name = '';
        });
        if (next === 3) {
          nextState.insurer_type_id =
            nextState.insurer_type_id || (param?.insurer_types[0]?.id?.toString() ?? '');
        }
        return nextState;
      });
    },
    [param]
  );

  const buildSearchPayload = useCallback(
    (overrides: Record<string, unknown> = {}): Record<string, unknown> => {
      const payload: Record<string, unknown> = {
        search_yearmonth: search.search_yearmonth,
        search_visitmonth: search.search_visitmonth || undefined,
        massager_id: search.massager_id ? Number(search.massager_id) : undefined,
        rezept_template_id: search.rezept_template_id
          ? Number(search.rezept_template_id)
          : undefined,
        rezept_kbn: search.rezept_kbn ? Number(search.rezept_kbn) : undefined,
        search_radio: search.search_radio,
        insurer_name: search.insurer_name || undefined,
        serial_number: search.serial_number || undefined,
        insurer_type_id: search.insurer_type_id ? Number(search.insurer_type_id) : undefined,
        ini: search.ini ? Number(search.ini) : undefined,
        patient_name: search.patient_name || undefined,
        sort_radio: search.sort_radio,
        ...overrides,
      };
      return Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined));
    },
    [search]
  );

  // 検索 always shows fresh data — same params would otherwise be served from cache, and
  // 報告書 / status fields must reflect changes made via hyperlinks (e.g. creating a
  // treatment_report from the 未 link). Mark exactly the query of `next` stale, then apply it:
  // same params → the active query refetches once; new params → the new query fetches once.
  // (Calling refetch() after setAppliedParams refetched the PREVIOUS params as well — two
  // requests per search whenever a filter had changed.)
  const applySearch = useCallback(
    (next: Record<string, unknown>) => {
      void queryClient.invalidateQueries({
        queryKey: ['rezept', 'list', tenant, clinicNumber],
        predicate: query => hashKey([query.queryKey[5]]) === hashKey([next]),
      });
      setAppliedParams(next);
    },
    [queryClient, tenant, clinicNumber]
  );

  const handleSubmitSearch = useCallback(() => {
    if (!search.search_yearmonth) {
      toast.error('提出年月を入力してください');
      return;
    }
    applySearch(buildSearchPayload());
  }, [buildSearchPayload, search.search_yearmonth, toast, applySearch]);

  const handleSubmitSyokan = useCallback(() => {
    if (!search.search_yearmonth) {
      toast.error('提出年月を入力してください');
      return;
    }
    applySearch(buildSearchPayload({ syokan_flg: true }));
  }, [buildSearchPayload, search.search_yearmonth, toast, applySearch]);

  const handleRezeptIssue = useCallback(async () => {
    const selectedRows = rows.filter(r => selectedIds.has(r.id));
    if (selectedRows.some(r => duplicateTreatmentReportFeeIds.has(r.id))) {
      appModal.warning({ content: DUPLICATE_TREATMENT_REPORT_FEE_MESSAGE });
      return;
    }

    const issuedIds = await issueRezepts(selectedRows, search.sort_radio);
    if (issuedIds) {
      queryClient.setQueryData<{
        rezepts: RezeptListItem[];
        print_num: number;
        is_attachement_document: boolean;
        is_visit_report_document: boolean;
      }>(['rezept', 'list', tenant, clinicNumber, serviceType, appliedParams], old => {
        if (!old) return old;
        const idSet = new Set(issuedIds);
        const newlyPrintedCount = old.rezepts.filter(r => idSet.has(r.id) && r.status === 0).length;
        return {
          ...old,
          rezepts: old.rezepts.map(r => (idSet.has(r.id) ? { ...r, status: 1 as const } : r)),
          print_num: Math.min(old.rezepts.length, old.print_num + newlyPrintedCount),
        };
      });
      setSelectedIds(new Set());
    }
  }, [
    rows,
    selectedIds,
    duplicateTreatmentReportFeeIds,
    appModal,
    search.sort_radio,
    issueRezepts,
    queryClient,
    tenant,
    clinicNumber,
    serviceType,
    appliedParams,
  ]);

  const { fetchGaijiListPdf } = useGaijiListPdf();

  // 外字リスト — re-run the search on the BE and open the 手書き修正対象者リスト
  // print page (gaiji_check = 1 patients) in a new window named gaiji_list_pdf.
  const handleGaijiList = useCallback(async () => {
    if (!search.search_yearmonth) {
      toast.error('提出年月を入力してください');
      return;
    }
    // Open the window synchronously so it is not blocked as a popup.
    const win = window.open('', 'gaiji_list_pdf');
    try {
      const html = await fetchGaijiListPdf(appliedParams ?? buildSearchPayload());
      if (win) {
        win.document.write(html);
        win.document.close();
      }
    } catch {
      win?.close();
    }
  }, [search.search_yearmonth, appliedParams, buildSearchPayload, fetchGaijiListPdf, toast]);

  const { mutateAsync: startVisitCheck, isPending: isStartingVisitCheck } =
    useStartVisitCheckMutation();

  const handleVisitCheck = useCallback(async () => {
    if (!search.search_yearmonth) {
      toast.error('提出年月を入力してください');
      return;
    }
    const payload: VisitCheckFilters = {
      ...(buildSearchPayload() as Omit<VisitCheckFilters, 'search_yearmonth'>),
      search_yearmonth: search.search_yearmonth,
    };
    const targetWindow = window.open('', 'visitCheck');
    targetWindow?.document.write(
      '<!doctype html><html><head><title>訪問データチェック</title></head><body style="font-family:sans-serif;padding:24px;">訪問データチェック中...</body></html>'
    );
    targetWindow?.document.close();
    try {
      const res = await startVisitCheck(payload);
      if (!res.has_data || !res.token) {
        targetWindow?.close();
        toast.warning('該当するデータはありませんでした。');
        return;
      }
      const resultPath =
        `/${tenant}/rezepts/visit_check_ready/${encodeURIComponent(res.rezept_ids)}` +
        `/${encodeURIComponent(res.search_yearmonth || payload.search_yearmonth)}` +
        `${res.search_visitmonth ? `/${encodeURIComponent(res.search_visitmonth)}` : ''}`;
      sessionStorage.setItem('visitCheckToken', res.token);
      if (targetWindow && !targetWindow.closed) {
        targetWindow.sessionStorage.setItem('visitCheckToken', res.token);
        targetWindow.location.href = resultPath;
        targetWindow.focus();
      } else {
        window.open(resultPath, 'visitCheck');
      }
    } catch (error) {
      targetWindow?.close();
      throw error;
    }
  }, [buildSearchPayload, search.search_yearmonth, startVisitCheck, tenant, toast]);

  const handlePrintAdjust = useCallback(() => {
    setPrintAdjustOpen(true);
  }, []);

  // 請求患者一覧 — BD: button is always enabled, DO NOT use selected checkboxes, print ALL
  // rezept matching the current filter. BE returns 204 when there is no data -> show popup
  // "該当するデータはありませんでした。" with an OK button. Data exists -> open a new PDF tab.
  // CP1: 請求金額 column = ￥0 placeholder; CP2-5 will port realtime calculation.
  const { mutateAsync: fetchBillPatientListPdf, isPending: isFetchingBillPatientList } =
    useBillPatientListPdf();
  const handleRezeptsInvoice = useCallback(async () => {
    if (!search.search_yearmonth) {
      toast.error('提出年月を入力してください');
      return;
    }
    // Open the tab synchronously so it is not blocked as a popup.
    const win = window.open('', 'bill_pt_pdf');
    try {
      const blob = await fetchBillPatientListPdf(appliedParams ?? buildSearchPayload());
      if (!blob) {
        win?.close();
        appModal.warning({ content: '該当するデータはありませんでした。' });
        return;
      }
      const url = URL.createObjectURL(blob);
      if (win) {
        win.location.href = url;
      } else {
        window.open(url, '_blank');
      }
    } catch {
      win?.close();
    }
  }, [
    search.search_yearmonth,
    appliedParams,
    buildSearchPayload,
    fetchBillPatientListPdf,
    appModal,
    toast,
  ]);

  const handleResetClick = useCallback(() => {
    if (selectedIds.size === 0) {
      appModal.warning({ content: '対象のレセプトを選択してください。' });
      return;
    }
    appModal.confirm({
      content: '選択されている全ての「印刷済み」を「未印刷」に戻します。よろしいですか？',
      okText: 'はい',
      cancelText: 'いいえ',
      onOk: async () => {
        await resetPrint(Array.from(selectedIds));
        toast.success('更新が正常に終了しました。');
        setSelectedIds(new Set());
        refetchList();
      },
    });
  }, [selectedIds, appModal, resetPrint, toast, refetchList]);

  // 確定 button — STEP 3-4: fetch confirm data
  const handleKakuteiClick = useCallback(async () => {
    if (!normalizeYearmonth(search.search_yearmonth)) {
      showInvalidYearmonthAlert();
      return;
    }
    try {
      const data = await kakuteiInit(search.search_yearmonth);
      setKakuteiConfirmData(data);
    } catch {
      toast.error('更新ができませんでした。');
    }
  }, [kakuteiInit, search.search_yearmonth, showInvalidYearmonthAlert, toast]);

  // 確定 button — STEP 6-7: execute after user clicks はい
  const handleKakuteiConfirm = useCallback(async () => {
    setKakuteiConfirmData(null);
    try {
      await kakuteiExecute(search.search_yearmonth);
      setKakuteiCompleteOpen(true);
    } catch {
      toast.error('更新ができませんでした。');
    }
  }, [kakuteiExecute, search.search_yearmonth, toast]);

  const handleTenki = useCallback(() => {
    if (selectedIds.size !== 1) {
      toast.error('転帰対象のデータを1件選択してください');
      return;
    }
    const [rezeptId] = Array.from(selectedIds);
    setTenkiRezeptId(rezeptId);
  }, [selectedIds, toast]);

  const handleTenkiClose = useCallback(() => {
    setTenkiRezeptId(null);
  }, []);

  const handleTenkiSuccess = useCallback(() => {
    setTenkiRezeptId(null);
    toast.success('更新が正常に終了しました。');
    refetchList();
  }, [toast, refetchList]);

  const handleToggleTreatmentReport = useCallback(
    (id: number, value: 0 | 1) => {
      const patchRows = (patch: (rows: RezeptListItem[]) => RezeptListItem[]) =>
        queryClient.setQueryData<{
          rezepts: RezeptListItem[];
          print_num: number;
          is_attachement_document: boolean;
          is_visit_report_document: boolean;
        }>(['rezept', 'list', tenant, clinicNumber, serviceType, appliedParams], old =>
          old ? { ...old, rezepts: patch(old.rezepts) } : old
        );

      // Optimistic: the clicked 本レセ, plus clearing the other unlocked sheets of the same
      // patient/month on tick. Linked 関レセ follow once the server answers.
      patchRows(rows => {
        const target = rows.find(r => r.id === id);
        return rows.map(r => {
          if (r.id === id) return { ...r, add_treatment_report: value };
          if (
            value === 1 &&
            target &&
            r.status === 0 &&
            r.patient_id === target.patient_id &&
            r.treatement_yearmonth === target.treatement_yearmonth
          ) {
            return { ...r, add_treatment_report: 0 };
          }
          return r;
        });
      });

      saveAddTreatmentReport({ id, value })
        .then(changes => {
          const byId = new Map(changes.map(c => [c.id, c.add_treatment_report]));
          patchRows(rows =>
            rows.map(r => (byId.has(r.id) ? { ...r, add_treatment_report: byId.get(r.id)! } : r))
          );
        })
        .catch(() => refetchList());
    },
    [
      appliedParams,
      clinicNumber,
      queryClient,
      refetchList,
      saveAddTreatmentReport,
      serviceType,
      tenant,
    ]
  );

  const handleSelectionChange = useCallback((ids: number[]) => {
    setSelectedIds(new Set(ids));
  }, []);

  const handleDoubleClickRow = useCallback((row: RezeptListItem) => {
    setEditStatusRezept(row);
  }, []);

  const printNum = listData?.print_num ?? param?.print_num ?? 0;
  const resultCount = rows.length;

  const content = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex items-center justify-center py-20">
          <AppSpin />
        </div>
      );
    }
    return (
      <>
        <RezeptSearchFilters
          search={search}
          param={param}
          massagerOptions={massagerOptions}
          printNum={printNum}
          resultCount={resultCount}
          onChange={handleFieldChange}
          onChangeSearchRadio={handleSearchRadioChange}
          onSubmitSearch={handleSubmitSearch}
          onSubmitSyokan={handleSubmitSyokan}
        />
        <CollapsibleCard>
          <div className="flex items-center gap-2 mb-3">
            {isR2 && <AppButton onClick={handleTenki}>転帰編集</AppButton>}
            <AppButton onClick={handlePrintAdjust}>印刷調整</AppButton>
          </div>

          <RezeptListTable
            rows={rows}
            isLoading={isListLoading}
            selectedIds={selectedIds}
            duplicateTreatmentReportFeeIds={duplicateTreatmentReportFeeIds}
            hasFilters={appliedParams !== null}
            onSelectionChange={handleSelectionChange}
            onDoubleClickRow={handleDoubleClickRow}
            onToggleTreatmentReport={handleToggleTreatmentReport}
          />
        </CollapsibleCard>
      </>
    );
  }, [
    isLoading,
    search,
    param,
    massagerOptions,
    printNum,
    resultCount,
    rows,
    duplicateTreatmentReportFeeIds,
    isListLoading,
    isR2,
    handleFieldChange,
    handleSearchRadioChange,
    handleSubmitSearch,
    handleSubmitSyokan,
    handleTenki,
    handlePrintAdjust,
    selectedIds,
    handleSelectionChange,
    handleDoubleClickRow,
    handleToggleTreatmentReport,
    appliedParams,
  ]);

  return (
    <div className="pb-4">
      {content}
      <RezeptListActionBar
        onRezept={handleRezeptIssue}
        onReset={handleResetClick}
        onRezeptsInvoice={handleRezeptsInvoice}
        onLongTerm={() => setLongTermOpen(true)}
        onGaijiList={handleGaijiList}
        onVisitCheck={handleVisitCheck}
        showVisitSummary={Boolean(hasVisitSummary)}
        onVisitSummary={handleVisitSummary}
        visitSummaryLoading={visitSummaryPdf.isPending}
        onKakutei={handleKakuteiClick}
      />

      {/* 訪問データチェック — start request loading + progress dialog */}
      <AppModal
        isOpen={isStartingVisitCheck}
        onClose={() => {}}
        footer={null}
        closable={false}
        maxWidth={200}
      >
        <div className="flex flex-col items-center gap-3 py-4">
          <AppSpin />
          <span className="text-sm">処理中...</span>
        </div>
      </AppModal>

      {/* loading overlay while processing */}
      <AppModal
        isOpen={isIssuing || isResetting || isFetchingBillPatientList}
        onClose={() => {}}
        footer={null}
        closable={false}
        maxWidth={200}
        zIndex={2001}
      >
        <div className="flex flex-col items-center gap-3 py-4">
          <AppSpin />
          <span className="text-sm">処理中...</span>
        </div>
      </AppModal>

      {/* 確定 confirm (STEP 5) */}
      {kakuteiConfirmData && (
        <AppModal
          isOpen
          title="確定"
          onClose={() => setKakuteiConfirmData(null)}
          onOk={handleKakuteiConfirm}
          okText="はい"
          cancelText="いいえ"
          maxWidth={520}
        >
          {/* HIT-4543: 「！注意」 caution header + all text bold (font-bold on wrapper). */}
          <div className="flex flex-col gap-2 font-bold">
            <p className="flex items-center">
              {IconUtils.getIcon('warning', true)}
              <span className="-ml-1.5">注意</span>
            </p>
            {kakuteiConfirmData.group_b_count > 0 ? (
              <>
                <p>{`${kakuteiConfirmData.search_yearmonth_wareki}提出の確定処理を行います。`}</p>
                <p className="text-red-500">
                  未印刷件数が{kakuteiConfirmData.group_b_count}件あります。
                </p>
                <p>
                  このまま処理を行うと、未印刷のデータは翌月提出に繰り越されますがよろしいですか？
                </p>
              </>
            ) : (
              <>
                <p>{`${kakuteiConfirmData.search_yearmonth_wareki}提出の確定処理を行います。`}</p>
                <p>
                  確定件数は
                  <span>{kakuteiConfirmData.group_a_count}</span>
                  件です。
                </p>
                <p>処理を実行してよろしいですか？</p>
              </>
            )}
          </div>
        </AppModal>
      )}

      {/* 確定 loading (STEP 4 / STEP 6 processing) */}
      <AppModal
        isOpen={isIniting || isExecuting}
        onClose={() => {}}
        footer={null}
        closable={false}
        maxWidth={200}
      >
        <div className="flex flex-col items-center gap-3 py-4">
          <AppSpin />
          <span className="text-sm">処理中...</span>
        </div>
      </AppModal>

      {/* 確定 complete (STEP 8) */}
      <AppModal
        isOpen={kakuteiCompleteOpen}
        title="レセプト発行おつかれ様でした"
        onClose={() => {
          setKakuteiCompleteOpen(false);
          refetchList();
        }}
        onOk={() => {
          setKakuteiCompleteOpen(false);
          refetchList();
        }}
        okText="OK"
        hideCancelButton
        maxWidth={800}
      >
        <div className="flex items-center gap-4">
          <img src="/images/thanks_icon.jpg" alt="" className="h-[300px] shrink-0" />
          <p className="font-bold" style={{ whiteSpace: 'pre-line' }}>
            {`レセプトデータを確定しました。\n\nレセプト提出期限前でしたら、出力したレセプト及びレセプトデータの修正が可能です。\n修正方法が不明の場合は、カスタマーサポートまでお問い合わせください。\n\n尚、レセプト提出期限後に作成したレセプトデータは、翌月以降にご提出ください。`}
          </p>
        </div>
      </AppModal>

      <LongTermPatientsModal
        isOpen={longTermOpen}
        onClose={() => setLongTermOpen(false)}
        searchYearmonth={
          appliedParams
            ? String(appliedParams.search_yearmonth ?? search.search_yearmonth)
            : search.search_yearmonth
        }
        massagerId={search.massager_id || undefined}
      />

      <PrintAdjustModal isOpen={printAdjustOpen} onClose={() => setPrintAdjustOpen(false)} />

      <EditRezeptStatusModal
        isOpen={editStatusRezept !== null}
        rezept={editStatusRezept}
        onClose={() => setEditStatusRezept(null)}
        onUpdated={() => {
          // BD: "The list screen fully reloads to reflect the new data
          //     (status, submission period, print count, etc.)."
          // useRezeptList uses React Query with the same queryKey, so spreading params
          // doesn't trigger a refetch (cache hit); call refetch() directly.
          refetchList();
        }}
      />

      <OutcomeEditModal
        isOpen={tenkiRezeptId !== null}
        rezeptId={tenkiRezeptId}
        onClose={handleTenkiClose}
        onUpdated={handleTenkiSuccess}
      />
    </div>
  );
};

export default RezeptListPage;
